import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetch, ApiError } from '../lib/api';
import { Avatar } from '../components/Avatar';
import BackButton from '../components/BackButton';
import { StatusBadge } from '../components/StatusBadge';
import { TimeEntryWidget } from '../components/TimeEntryWidget';
import { MessageThread } from '../components/MessageThread';
import { validateIsraeliId, validateCompanyNumber } from '../lib/israeli-id';
import { ReminderRow } from '../components/ReminderRow';
import type {
  CaseStatus,
  ClientComment,
  ClientReminder,
  ClientSummary,
  ClientType,
} from '../types';

type ClientDetailData = ClientSummary & {
  cases: Array<{
    id: string;
    caseNumber: string;
    area: string | null;
    status: CaseStatus;
    openedAt: string;
    closedAt: string | null;
  }>;
  comments: ClientComment[];
  reminders: ClientReminder[];
};

const TYPE_OPTIONS: { value: ClientType; labelKey: string }[] = [
  { value: 'private', labelKey: 'types.private' },
  { value: 'business', labelKey: 'types.business' },
  { value: 'government', labelKey: 'types.government_short' },
];

// See AddClient.tsx for the rationale: tag values stay Hebrew (DB) while the
// label is localized.
const TAG_OPTIONS: { value: string; labelKey: string }[] = [
  { value: 'VIP', labelKey: 'tags.vip' },
  { value: 'הפניה', labelKey: 'tags.referral' },
  { value: 'לקוח חוזר', labelKey: 'tags.returning' },
  { value: 'תשלום מיידי', labelKey: 'tags.instant_payment' },
  { value: 'דחוף', labelKey: 'tags.urgent' },
];

export function ClientDetail({
  clientId,
  firmId,
  onBack,
  onAddCase,
  onDeleted,
}: {
  clientId: string;
  firmId: string;
  onBack: () => void;
  onAddCase: (clientId: string, clientName: string) => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation(['clients', 'common', 'wa']);
  const [data, setData] = useState<ClientDetailData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // Set of case IDs whose timer widget is currently expanded
  const [openTimers, setOpenTimers] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    apiFetch<ClientDetailData>(`/api/clients/${clientId}`)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [clientId]);

  if (error) return <div className="empty">{t('common:errors.with_message', { message: error })}</div>;
  if (!data) return <div className="empty">{t('common:actions.loading')}</div>;

  if (editing) {
    return (
      <EditClient
        client={data}
        onSaved={(updated) => {
          setData({ ...data, ...updated });
          setEditing(false);
        }}
        onCancel={() => setEditing(false)}
        onDeleted={onDeleted}
      />
    );
  }

  return (
    <div className="form-wrap">
      <div className="form-card">
        <div className="form-card-header">
          <Avatar name={data.name} size="lg" />
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{data.name}</div>
            <div className="form-preview-type">{t(`types.${data.type}`)}</div>
          </div>
          <button
            className="btn-cancel"
            onClick={() => setEditing(true)}
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none', marginInlineEnd: 6 }}
          >
            {t('common:actions.edit')}
          </button>
          <BackButton
            onClick={onBack}
            className="btn-cancel"
            style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', border: 'none' }}
          />
        </div>

        <div className="form-section">
          <div className="section-label">{t('detail.section_contact')}</div>
          <dl className="kv">
            {data.phone && <KV k={t('detail.field_phone')}>{data.phone}</KV>}
            {data.email && <KV k={t('detail.field_email')}>{data.email}</KV>}
            {data.address && <KV k={t('detail.field_address')}>{data.address}</KV>}
            {data.idNumber && (
              <KV k={data.type === 'business' ? t('detail.field_id_business') : t('detail.field_id_personal')}>
                {data.idNumber}
              </KV>
            )}
            {data.birthDate && <KV k={t('detail.field_birth_date')}>{data.birthDate}</KV>}
            {data.source && <KV k={t('detail.field_source')}>{data.source}</KV>}
          </dl>
        </div>

        {data.tags.length > 0 && (
          <div className="form-section">
            <div className="section-label">{t('detail.section_tags')}</div>
            <div className="tag-row">
              {data.tags.map((tag) => {
                const opt = TAG_OPTIONS.find((o) => o.value === tag);
                return (
                  <div key={tag} className="tag active">
                    {opt ? t(opt.labelKey) : tag}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data.notes && (
          <div className="form-section">
            <div className="section-label">{t('detail.section_notes')}</div>
            <p style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{data.notes}</p>
          </div>
        )}

        <div className="form-section">
          <div className="section-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{t('detail.cases_heading', { count: data.cases.length })}</span>
            <button className="btn-link" onClick={() => onAddCase(data.id, data.name)}>
              {t('detail.add_case')}
            </button>
          </div>
          {data.cases.length === 0 ? (
            <div className="empty" style={{ padding: '24px 0' }}>
              {t('detail.no_cases')}
            </div>
          ) : (
            data.cases.map((c) => {
              const timerOpen = openTimers.has(c.id);
              return (
                <div key={c.id} style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, marginBottom: 8 }}>
                  <div className="case-row" style={{ borderBottom: 'none', padding: '8px 0' }}>
                    <div className="case-info">
                      <div className="case-name">{c.area ?? c.caseNumber}</div>
                      <div className="case-sub">
                        {c.caseNumber} · {c.openedAt.slice(0, 10)}
                      </div>
                    </div>
                    <button
                      className="btn-link"
                      style={{ marginInlineEnd: 8 }}
                      onClick={() =>
                        setOpenTimers((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          return next;
                        })
                      }
                    >
                      ⏱
                    </button>
                    <StatusBadge
                      caseId={c.id}
                      status={c.status}
                      onChanged={(newStatus) =>
                        setData({
                          ...data,
                          cases: data.cases.map((x) => (x.id === c.id ? { ...x, status: newStatus } : x)),
                        })
                      }
                    />
                  </div>
                  {timerOpen && (
                    <TimeEntryWidget
                      caseId={c.id}
                      caseLabel={`${c.caseNumber} · ${c.area ?? ''}`}
                    />
                  )}
                </div>
              );
            })
          )}
        </div>

        <ClientNotesToSelf
          clientId={data.id}
          comments={data.comments}
          onChange={(comments) => setData({ ...data, comments })}
        />

        <ClientReminders
          clientId={data.id}
          reminders={data.reminders}
          onChange={(reminders) => setData({ ...data, reminders })}
        />

        <div className="form-section">
          <div className="section-label">{t('wa:thread.title')}</div>
          <MessageThread
            clientId={data.id}
            clientName={data.name}
            clientPhone={data.phone}
            firmId={firmId}
          />
        </div>
      </div>
    </div>
  );
}

function ClientNotesToSelf({
  clientId,
  comments,
  onChange,
}: {
  clientId: string;
  comments: ClientComment[];
  onChange: (next: ClientComment[]) => void;
}) {
  const { t, i18n } = useTranslation('clients');
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    const body = draft.trim();
    if (!body || busy) return;
    setBusy(true);
    try {
      const c = await apiFetch<ClientComment>(`/api/clients/${clientId}/comments`, {
        method: 'POST',
        body: JSON.stringify({ body }),
      });
      onChange([c, ...comments]);
      setDraft('');
    } finally {
      setBusy(false);
    }
  }

  async function del(cid: string) {
    await apiFetch(`/api/clients/${clientId}/comments/${cid}`, { method: 'DELETE' });
    onChange(comments.filter((c) => c.id !== cid));
  }

  return (
    <div className="form-section">
      <div className="section-label">{t('detail.notes_to_self')}</div>
      <div className="field">
        <textarea
          rows={3}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={t('detail.notes_to_self_placeholder')}
          maxLength={5000}
        />
      </div>
      <div style={{ marginTop: 6, textAlign: 'end' }}>
        <button
          className="btn-primary"
          onClick={() => void add()}
          disabled={busy || !draft.trim()}
        >
          {t('detail.notes_to_self_add')}
        </button>
      </div>
      {comments.length === 0 ? (
        <div className="empty" style={{ padding: '12px 0' }}>
          {t('detail.notes_to_self_empty')}
        </div>
      ) : (
        comments.map((c) => (
          <div
            key={c.id}
            className="case-row"
            style={{ alignItems: 'flex-start', padding: '8px 0', borderBottom: '1px solid var(--border)' }}
          >
            <div style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                {new Date(c.createdAt).toLocaleString(i18n.language, {
                  day: '2-digit',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
              <div>{c.body}</div>
            </div>
            <button className="btn-cancel" onClick={() => void del(c.id)}>
              {t('detail.notes_to_self_delete')}
            </button>
          </div>
        ))
      )}
    </div>
  );
}

function ClientReminders({
  clientId,
  reminders,
  onChange,
}: {
  clientId: string;
  reminders: ClientReminder[];
  onChange: (next: ClientReminder[]) => void;
}) {
  const { t, i18n } = useTranslation('clients');
  const [remindAt, setRemindAt] = useState(() => defaultRemindAt());
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!remindAt || busy) return;
    setBusy(true);
    try {
      const at = new Date(remindAt);
      const r = await apiFetch<ClientReminder>(`/api/clients/${clientId}/reminders`, {
        method: 'POST',
        body: JSON.stringify({
          remindAt: at.toISOString(),
          note: note.trim() || undefined,
        }),
      });
      onChange(
        [...reminders, r].sort((a, b) => a.remindAt.localeCompare(b.remindAt)),
      );
      setNote('');
      setRemindAt(defaultRemindAt());
    } finally {
      setBusy(false);
    }
  }

  async function del(rid: string) {
    await apiFetch(`/api/clients/${clientId}/reminders/${rid}`, { method: 'DELETE' });
    onChange(reminders.filter((r) => r.id !== rid));
  }

  async function saveEdit(rid: string, next: { remindAt: string; note: string | null }) {
    const updated = await apiFetch<ClientReminder>(`/api/clients/${clientId}/reminders/${rid}`, {
      method: 'PATCH',
      body: JSON.stringify(next),
    });
    onChange(
      reminders
        .map((r) => (r.id === rid ? updated : r))
        .sort((a, b) => a.remindAt.localeCompare(b.remindAt)),
    );
  }

  return (
    <div className="form-section">
      <div className="section-label">{t('detail.reminders')}</div>
      <div className="field-grid reminder-add-grid" style={{ gridTemplateColumns: '1fr 2fr auto' }}>
        <div className="field">
          <label>{t('detail.reminder_at')}</label>
          <input
            type="datetime-local"
            value={remindAt}
            onChange={(e) => setRemindAt(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t('detail.reminder_note')}</label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={t('detail.reminder_note_placeholder')}
            maxLength={500}
          />
        </div>
        <div className="field" style={{ alignSelf: 'end' }}>
          <button className="btn-primary" onClick={() => void add()} disabled={busy || !remindAt}>
            {t('detail.reminder_add')}
          </button>
        </div>
      </div>
      {reminders.length === 0 ? (
        <div className="empty" style={{ padding: '12px 0' }}>
          {t('detail.reminders_empty')}
        </div>
      ) : (
        reminders.map((r) => (
          <ReminderRow
            key={r.id}
            reminder={r}
            labels={{
              edit: t('detail.reminder_edit'),
              delete: t('detail.reminder_delete'),
              save: t('detail.reminder_save'),
              editCancel: t('detail.reminder_edit_cancel'),
              notePlaceholder: t('detail.reminder_note_placeholder'),
              pendingPush: t('detail.reminder_pending_push'),
            }}
            onSave={(next) => saveEdit(r.id, next)}
            onDelete={() => del(r.id)}
          />
        ))
      )}
    </div>
  );
}

function defaultRemindAt(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EditClient({
  client,
  onSaved,
  onCancel,
  onDeleted,
}: {
  client: ClientDetailData;
  onSaved: (updated: ClientSummary) => void;
  onCancel: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation(['clients', 'common']);
  const [type, setType] = useState<ClientType>(client.type);
  const [name, setName] = useState(client.name);
  const [idNumber, setIdNumber] = useState(client.idNumber ?? '');
  const [phone, setPhone] = useState(client.phone ?? '');
  const [email, setEmail] = useState(client.email ?? '');
  const [address, setAddress] = useState(client.address ?? '');
  const [birthDate, setBirthDate] = useState(client.birthDate ?? '');
  const [tags, setTags] = useState<string[]>(client.tags);
  const [notes, setNotes] = useState(client.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idHint = useMemo(() => {
    if (!idNumber) return null;
    return type === 'business' ? validateCompanyNumber(idNumber) : validateIsraeliId(idNumber);
  }, [idNumber, type]);

  function toggleTag(tag: string) {
    setTags((prev) => (prev.includes(tag) ? prev.filter((tt) => tt !== tag) : [...prev, tag]));
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const updated = await apiFetch<ClientSummary>(`/api/clients/${client.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          type,
          name: name.trim(),
          idNumber: idNumber || null,
          phone: phone || null,
          email: email || null,
          address: address || null,
          birthDate: birthDate || null,
          tags,
          notes: notes || null,
        }),
      });
      onSaved(updated);
    } catch (err) {
      if (err instanceof ApiError) {
        const translated = t(`errors.${err.code}`, { defaultValue: '' });
        setError(translated || err.code);
      } else {
        setError(err instanceof Error ? err.message : t('common:errors.generic'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete() {
    if (!confirm(t('form.delete_confirm', { name: client.name }))) return;
    setBusy(true);
    try {
      await apiFetch(`/api/clients/${client.id}`, { method: 'DELETE' });
      onDeleted();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('common:errors.generic'));
      setBusy(false);
    }
  }

  return (
    <div className="form-wrap">
      <form className="form-card" onSubmit={handleSubmit}>
        <div className="form-card-header">
          <Avatar name={name || client.name} size="lg" />
          <div style={{ flex: 1 }}>
            <div className="form-preview-name">{t('form.edit_title')}</div>
            <div className="form-preview-type">{t(`types.${type}`)}</div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_type')}</div>
          <div className="type-row">
            {TYPE_OPTIONS.map((opt) => (
              <div
                key={opt.value}
                className={`type-btn ${type === opt.value ? 'active' : ''}`}
                onClick={() => setType(opt.value)}
              >
                {t(opt.labelKey)}
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_personal')}</div>
          <div className="field-grid">
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>{t('form.full_name')} <span className="req">*</span></label>
              <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} />
            </div>
            <div className="field">
              <label>
                {type === 'business' ? t('form.id_number_business') : t('form.id_number_personal')}
              </label>
              <input
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value.replace(/\D/g, '').slice(0, 9))}
                placeholder={t('form.id_placeholder')}
                inputMode="numeric"
              />
              {idHint && idNumber.length >= 4 && (
                <small className={idHint.valid ? 'slug-status ok' : 'slug-status err'}>
                  {idHint.valid
                    ? `✓ ${t('id_validation.valid')}`
                    : t(`id_validation.${idHint.reason}`, { defaultValue: '' })}
                </small>
              )}
            </div>
            <div className="field">
              <label>{t('form.birth_date')}</label>
              <input type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_contact')}</div>
          <div className="field-grid">
            <div className="field">
              <label>{t('form.phone')}</label>
              <input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="field">
              <label>{t('form.email')}</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field" style={{ gridColumn: '1 / -1' }}>
              <label>{t('form.address')}</label>
              <input value={address} onChange={(e) => setAddress(e.target.value)} />
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_tags')}</div>
          <div className="tag-row">
            {TAG_OPTIONS.map((tag) => (
              <div
                key={tag.value}
                className={`tag ${tags.includes(tag.value) ? 'active' : ''}`}
                onClick={() => toggleTag(tag.value)}
              >
                {t(tag.labelKey)}
              </div>
            ))}
          </div>
        </div>

        <div className="form-section">
          <div className="section-label">{t('form.section_notes')}</div>
          <div className="field">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        {error && (
          <div className="form-section">
            <div className="signin-error">{error}</div>
          </div>
        )}

        <div className="form-footer">
          <button type="submit" className="btn-save" disabled={busy}>
            {busy ? t('common:actions.loading_dots') : t('form.save_changes')}
          </button>
          <button type="button" className="btn-cancel" onClick={onCancel} disabled={busy}>
            {t('common:actions.cancel')}
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={busy}
            className="btn-danger"
            style={{ marginInlineStart: 'auto' }}
          >
            {t('form.delete_button')}
          </button>
        </div>
      </form>
    </div>
  );
}

function KV({ k, children }: { k: string; children: React.ReactNode }) {
  return (
    <div className="kv-row">
      <dt>{k}</dt>
      <dd>{children}</dd>
    </div>
  );
}
